import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 3-1: Long overworld, lots of pipes, many koopas
export function buildLevel3_1(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [16, 17],
    [32, 34],
    [50, 52],
    [68, 69],
    [86, 88],
    [104, 105],
    [122, 124],
    [140, 142],
    [158, 159]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  setTile(10, 4, TileType.Question, { kind: "mushroom" });

  const pipes: Array<[number, number]> = [
    [14, 2],
    [26, 3],
    [38, 4],
    [54, 2],
    [72, 3],
    [88, 4],
    [106, 2],
    [124, 3],
    [142, 4],
    [160, 2],
    [170, 3],
    [180, 4]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // brick formations
  setTile(20, 4, TileType.Brick);
  setTile(21, 4, TileType.Brick);
  setTile(22, 4, TileType.Question, { kind: "coin" });
  setTile(23, 4, TileType.Brick);

  setTile(42, 4, TileType.Brick);
  setTile(43, 4, TileType.Brick);
  setTile(44, 4, TileType.Brick);
  setTile(45, 4, TileType.Question, { kind: "coin" });
  setTile(46, 4, TileType.Brick);

  for (let x = 58; x <= 63; x++) setTile(x, 4, TileType.Brick);
  setTile(60, 4, TileType.Question, { kind: "mushroom" });
  setTile(62, 4, TileType.Question, { kind: "coin" });

  for (let x = 76; x <= 82; x++) setTile(x, 4, TileType.Brick);
  setTile(78, 4, TileType.Question, { kind: "coin" });
  setTile(80, 4, TileType.Question, { kind: "coin" });

  for (let x = 94; x <= 100; x++) setTile(x, 4, TileType.Brick);
  setTile(96, 4, TileType.Question, { kind: "mushroom" });
  setTile(98, 4, TileType.Question, { kind: "coin" });

  for (let x = 110; x <= 116; x++) setTile(x, 4, TileType.Brick);
  setTile(112, 4, TileType.Question, { kind: "coin" });
  setTile(114, 4, TileType.Question, { kind: "coin" });

  for (let x = 128; x <= 134; x++) setTile(x, 4, TileType.Brick);
  setTile(130, 4, TileType.Question, { kind: "mushroom" });
  setTile(132, 4, TileType.Question, { kind: "coin" });

  setTile(146, 4, TileType.Brick);
  setTile(147, 4, TileType.Brick);
  setTile(148, 4, TileType.Brick);
  setTile(149, 4, TileType.Question, { kind: "coin" });

  setTile(164, 4, TileType.Brick);
  setTile(165, 4, TileType.Brick);
  setTile(166, 4, TileType.Brick);

  // high ?
  setTile(30, 7, TileType.Question, { kind: "coin" });
  setTile(48, 7, TileType.Question, { kind: "coin" });
  setTile(66, 7, TileType.Question, { kind: "coin" });
  setTile(84, 7, TileType.Question, { kind: "coin" });
  setTile(102, 7, TileType.Question, { kind: "coin" });
  setTile(120, 7, TileType.Question, { kind: "coin" });
  setTile(138, 7, TileType.Question, { kind: "coin" });

  // staircase
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 14 },
    { kind: "koopa", x: 18 },
    { kind: "goomba", x: 24 },
    { kind: "goomba", x: 30 },
    { kind: "koopa", x: 36 },
    { kind: "goomba", x: 42 },
    { kind: "goomba", x: 48 },
    { kind: "koopa", x: 54 },
    { kind: "goomba", x: 60 },
    { kind: "goomba", x: 66 },
    { kind: "koopa", x: 72 },
    { kind: "goomba", x: 78 },
    { kind: "goomba", x: 84 },
    { kind: "koopa", x: 90 },
    { kind: "goomba", x: 96 },
    { kind: "goomba", x: 102 },
    { kind: "koopa", x: 108 },
    { kind: "goomba", x: 114 },
    { kind: "goomba", x: 120 },
    { kind: "koopa", x: 126 },
    { kind: "goomba", x: 132 },
    { kind: "goomba", x: 138 },
    { kind: "koopa", x: 144 },
    { kind: "goomba", x: 150 },
    { kind: "goomba", x: 156 },
    { kind: "koopa", x: 162 },
    { kind: "goomba", x: 168 },
    { kind: "goomba", x: 174 },
    { kind: "koopa", x: 180 },
    { kind: "goomba", x: 186 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
